import React, { useState } from "react";
import { Form, Row, Col, FloatingLabel, Button, Modal } from "react-bootstrap";
import axios from "axios";
import DatePicker from "react-datepicker";
import { format } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import GLOBALVAR from "../../constants";
import LoadingSpinner from "../../components/misc/LoadingSpinner";

function EditLoan({ loanProcessingData, updateParentLoanProcessing }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dateGranted, setDateGranted] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalHeader, setModalHeader] = useState(null);
    const [modalBody, setModalBody] = useState(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [errMessages, setErrMessages] = useState(false);
    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);

    const [formData, setFormData] = useState({
        id: loanProcessingData ? loanProcessingData.id : '',
        loanProcessingType: loanProcessingData ? loanProcessingData.loanProcessingType : '',
        amountGranted: loanProcessingData ? loanProcessingData.amountGranted : '',
        dateGranted: loanProcessingData ? loanProcessingData.dateGranted : '',
        interestRate: loanProcessingData ? loanProcessingData.interestRate : '',
        loanTerm: loanProcessingData ? loanProcessingData.loanTerm : '',
        paymentType: loanProcessingData ? loanProcessingData.paymentType : '',
        loanInterval: loanProcessingData ? loanProcessingData.loanInterval : '',
        // Add the missing fields
        serviceFee: loanProcessingData ? loanProcessingData.serviceFee : '',
        notarialFee: loanProcessingData ? loanProcessingData.notarialFee : '',
        docStamp: loanProcessingData ? loanProcessingData.docStamp : '',
        insurance: loanProcessingData ? loanProcessingData.insurance : '',
        // Continue with existing fields
        accountOfficer: loanProcessingData ? loanProcessingData.accountOfficer : '',
        loanProduct: loanProcessingData ? loanProcessingData.loanProduct : '',
        loanLedger: loanProcessingData ? loanProcessingData.loanLedger : '',
        loanSecurity: loanProcessingData ? loanProcessingData.loanSecurity : '',
        reference: loanProcessingData ? loanProcessingData.reference : '',
        loanType: loanProcessingData ? loanProcessingData.loanType : '',
        addtl_beneficiary: loanProcessingData ? loanProcessingData.addtl_beneficiary : '',
        addtl_contact: loanProcessingData ? loanProcessingData.addtl_contact : '',
        addtl_relationship: loanProcessingData ? loanProcessingData.addtl_relationship : '',
        applicant: loanProcessingData ? loanProcessingData.applicant : '',
        advanceInterest: loanProcessingData ? loanProcessingData.advanceInterest : true
    });

    const handleDateChange = (date) => {
        if(!date) return;

        setDateGranted(date);
        const formattedDate = format(date, 'MM/dd/yyyy'); 
        handleChangeInput({ target: { name: "dateGranted", value: formattedDate } });
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true); // Set submitting state to true
        setShowSpinner(true);

        console.log(formData);

        try {
            const response = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/update/${formData.id}`, formData);
            setModalHeader('Success');
            setModalBody(response.data.message);
            handleShow();
            updateParentLoanProcessing(formData);
        } catch (error) {
            console.error('Error updating loan processing entry:', error);
            setModalHeader('Validation Error');

            const errorsArray = error.response.data.error.split(',\n');
            const filteredErrors = errorsArray.filter(error => error.trim() !== '');
            setErrMessages(filteredErrors);
            handleShow();
        } finally {
            setIsSubmitting(false); // Reset submitting state
            setShowSpinner(false);
        }
    };

    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <Modal 
                show={showModal} 
                onHide={handleClose}
                size="lg"
                centered
                className="custom-modal"
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>{modalHeader}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errMessages ? (
                        <div className="alert alert-danger">
                            <ul className="mb-0">
                                {errMessages.map((error, index) => (
                                    <li key={index}>{error.trim().replace('Validation error: ', '')}</li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="alert alert-success">{modalBody}</div>
                    )}
                </Modal.Body>
            </Modal>

            <Form onSubmit={handleSubmit} className="edit-loan-form">
                {/* Loan Type Section */}
                <div className="form-section">
                    <h5 className="section-title">Loan Classification</h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingLoanProcessingType" label="Type of Loan *">
                                <Form.Select 
                                    name="loanProcessingType" 
                                    onChange={handleChangeInput}
                                    value={formData.loanProcessingType}
                                    required
                                >
                                    <option value="">Select Type of Loan</option>
                                    <option value="new">New Loan</option>
                                    <option value="reloan">Reloan</option>
                                    <option value="restructure">Restructure</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingLoanType" label="Loan Type *">
                                <Form.Select 
                                    name="loanType" 
                                    onChange={handleChangeInput}
                                    value={formData.loanType}
                                    required
                                >
                                    <option value="">Select Loan Type</option>
                                    <option value="personal">Personal Loan</option>
                                    <option value="business">Business Loan</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>
                </div>

                {/* Loan Details Section */}
                <div className="form-section">
                    <h5 className="section-title">Loan Details</h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingAmountGranted" label="Amount Granted *">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="amountGranted"
                                    placeholder="Amount Granted"
                                    value={formData.amountGranted}
                                    onChange={handleChangeInput}
                                    required
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel 
                                controlId="floatingDateGranted" 
                                label={dateGranted || loanProcessingData?.dateGranted ? '' : 'Date Granted *'}
                            >
                                <DatePicker
                                    className="form-control"
                                    selected={dateGranted || (loanProcessingData?.dateGranted ? new Date(loanProcessingData.dateGranted) : null)}
                                    onChange={handleDateChange}
                                    dateFormat="MM/dd/yyyy"
                                    maxDate={new Date()}
                                    required
                                    placeholderText={dateGranted || loanProcessingData?.dateGranted ? '' : 'Select date granted'}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={4}>
                            <FloatingLabel controlId="floatingInterestRate" label="Interest Rate (%) *">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="interestRate"
                                    placeholder="Interest Rate"
                                    value={formData.interestRate}
                                    onChange={handleChangeInput}
                                    required
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={4}>
                            <FloatingLabel controlId="floatingLoanTerm" label="Loan Term *">
                                <Form.Control 
                                    type="number"
                                    min="1"
                                    name="loanTerm"
                                    placeholder="Loan Term"
                                    value={formData.loanTerm}
                                    onChange={handleChangeInput}
                                    required
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={4}>
                            <FloatingLabel controlId="floatingPaymentType" label="Payment Type *">
                                <Form.Select 
                                    name="paymentType"
                                    onChange={handleChangeInput}
                                    value={formData.paymentType}
                                    required
                                >
                                    <option value="">Select Payment Type</option>
                                    <option value="add">Add On</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col>
                            <FloatingLabel controlId="floatingLoanInterval" label="Payment Interval *">
                                <Form.Select 
                                    name="loanInterval"
                                    onChange={handleChangeInput}
                                    value={formData.loanInterval}
                                    required
                                >
                                    <option value="">Select Payment Interval</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="bimonthly">Bi-Monthly</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="semiannual">Semi-Annual</option>
                                    <option value="yearly">Yearly</option>
                                    <option value="lumpsum">Lumpsum</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>
                </div>

                {/* Fees Section */}
                <div className="form-section">
                    <h5 className="section-title">Fees & Charges</h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingServiceFee" label="Service Fee">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="serviceFee"
                                    placeholder="Service Fee"
                                    value={formData.serviceFee}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingNotarialFee" label="Notarial Fee">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="notarialFee"
                                    placeholder="Notarial Fee"
                                    value={formData.notarialFee}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={4}>
                            <FloatingLabel controlId="floatingDocStamp" label="Doc Stamp">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="docStamp"
                                    placeholder="Doc Stamp"
                                    value={formData.docStamp}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={4}>
                            <FloatingLabel controlId="floatingInsurance" label="Insurance">
                                <Form.Control 
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="insurance"
                                    placeholder="Insurance"
                                    value={formData.insurance}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={4}>
                            <FloatingLabel controlId="floatingAdvanceInterest" label="Advance Interest">
                                <Form.Check
                                    type="switch"
                                    id="advanceInterestSwitch"
                                    name="advanceInterest"
                                    checked={formData.advanceInterest}
                                    onChange={(e) => handleChangeInput({
                                        target: {
                                            name: 'advanceInterest',
                                            value: e.target.checked
                                        }
                                    })}
                                    className="mt-3"
                                    label={formData.advanceInterest ? "Yes" : "No"}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>
                </div>

                {/* Loan Product Details */}
                <div className="form-section">
                    <h5 className="section-title">Product Details</h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingLoanProduct" label="Loan Product *">
                                <Form.Select 
                                    name="loanProduct"
                                    onChange={handleChangeInput}
                                    value={formData.loanProduct}
                                    required
                                >
                                    <option value="">Select Loan Product</option>
                                    <option value="regular">Regular Loan</option>
                                    <option value="salary">Salary Loan</option>
                                    <option value="pension">Pension Loan</option>
                                    <option value="honorarium">Honorarium Loan</option>
                                    <option value="market">Market Vendors</option>
                                    <option value="clean">Clean Loan</option>
                                    <option value="chattel">Chattel</option>
                                    <option value="rem">REM</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingLoanSecurity" label="Loan Security *">
                                <Form.Select 
                                    name="loanSecurity"
                                    onChange={handleChangeInput}
                                    value={formData.loanSecurity}
                                    required
                                >
                                    <option value="">Select Loan Security</option>
                                    <option value="unsecured">Unsecured</option>
                                    <option value="atm">Secured with ATM</option>
                                    <option value="pdc">Secured with PDC</option>
                                    <option value="clean">Clean Loan</option>
                                    <option value="chattel">Chattel Mortgage</option>
                                    <option value="residential">Real Estate Mortgage - Residential</option>
                                    <option value="commercial">Real Estate Mortgage - Commercial</option>
                                    <option value="others">Others</option>
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingLoanLedger" label="Loan Ledger">
                                <Form.Control 
                                    type="text"
                                    name="loanLedger"
                                    placeholder="Loan Ledger"
                                    value={formData.loanLedger}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingAccountOfficer" label="Account Officer *">
                                <Form.Control 
                                    type="text"
                                    name="accountOfficer"
                                    placeholder="Account Officer"
                                    value={formData.accountOfficer}
                                    onChange={handleChangeInput}
                                    required
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>
                </div>

                {/* Additional Information */}
                <div className="form-section">
                    <h5 className="section-title">Additional Information</h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingBeneficiary" label="Beneficiary Name">
                                <Form.Control 
                                    type="text"
                                    name="addtl_beneficiary"
                                    placeholder="Beneficiary Name"
                                    value={formData.addtl_beneficiary}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingContact" label="Contact Number">
                                <Form.Control 
                                    type="text"
                                    name="addtl_contact"
                                    placeholder="Contact Number"
                                    value={formData.addtl_contact}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <FloatingLabel controlId="floatingRelationship" label="Relationship">
                                <Form.Control 
                                    type="text"
                                    name="addtl_relationship"
                                    placeholder="Relationship"
                                    value={formData.addtl_relationship}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col md={6}>
                            <FloatingLabel controlId="floatingReference" label="Reference">
                                <Form.Control 
                                    type="text"
                                    name="reference"
                                    placeholder="Reference"
                                    value={formData.reference}
                                    onChange={handleChangeInput}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>
                </div>

                <div className="form-actions">
                    <Button 
                        variant="primary" 
                        type="submit" 
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Updating...' : 'Update Loan'}
                    </Button>
                </div>
            </Form>
        </>
    );
}

export default EditLoan;
